import { useQuery } from '@tanstack/react-query';
import { Mail, CheckCircle2, AlertCircle, Clock, Send } from 'lucide-react';
import { EmptyState, Skeleton, Badge } from '../../../../ui';
import { leadsService } from '../../../../services/leads.service';

interface CampaignSummary {
  id: number;
  name: string;
  status: string;
  sentAt: string | null;
  scheduledAt: string | null;
  createdAt: string;
  template?: { id: number; name: string; subject: string } | null;
}

interface EmailHistoryEntry {
  id: number;
  campaignId: number;
  status: 'PENDING' | 'SENT' | 'FAILED' | string;
  sentAt: string | null;
  errorMessage: string | null;
  email: string;
  campaign: CampaignSummary | null;
}

const STATUS_TONE: Record<string, 'info' | 'danger' | 'warning' | 'neutral'> = {
  SENT: 'info',
  FAILED: 'danger',
  PENDING: 'warning',
};

const STATUS_LABEL: Record<string, string> = {
  SENT: 'Enviado',
  FAILED: 'Falhou',
  PENDING: 'Pendente',
};

function StatusIcon({ status }: { status: string }) {
  if (status === 'SENT') {
    return <CheckCircle2 className="w-4 h-4 text-accent" />;
  }
  if (status === 'FAILED') {
    return <AlertCircle className="w-4 h-4 text-danger" />;
  }
  return <Clock className="w-4 h-4 text-warning" />;
}

function formatDate(value: string | null | undefined): string {
  if (!value) {
    return '—';
  }
  try {
    return new Date(value).toLocaleString('pt-BR');
  } catch {
    return '—';
  }
}

export function EmailTab({ leadId, leadEmail }: { leadId: number; leadEmail: string | null }) {
  const { data, isLoading } = useQuery({
    queryKey: ['lead', leadId, 'email-history'],
    queryFn: () => leadsService.getEmailHistory(leadId) as Promise<EmailHistoryEntry[]>,
    enabled: !!leadId,
  });

  if (isLoading) {
    return <Skeleton height={180} />;
  }

  const entries = Array.isArray(data) ? data : [];

  if (!leadEmail && entries.length === 0) {
    return (
      <EmptyState
        illustration={<Mail className="w-12 h-12" />}
        title="Sem email cadastrado"
        body="Cadastre um email no lead para receber campanhas."
      />
    );
  }

  if (entries.length === 0) {
    return (
      <EmptyState
        illustration={<Mail className="w-12 h-12" />}
        title={`Sem histórico para ${leadEmail}`}
        body="Quando este lead for incluído em uma campanha, ela aparecerá aqui."
      />
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-xs text-ink-tertiary">
        {entries.length} envio{entries.length !== 1 ? 's' : ''} para {leadEmail ?? 'este lead'}
      </p>
      <ul className="divide-y divide-border rounded-lg border border-border bg-surface">
        {entries.map((entry) => {
          const c = entry.campaign;
          const tone = STATUS_TONE[entry.status] ?? 'neutral';
          const label = STATUS_LABEL[entry.status] ?? entry.status;
          return (
            <li key={entry.id} className="px-4 py-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <Send className="w-3.5 h-3.5 text-ink-tertiary shrink-0" />
                    <span className="text-sm font-medium text-ink-primary truncate">
                      {c?.name ?? `Campanha #${entry.campaignId}`}
                    </span>
                  </div>
                  {c?.template?.subject && (
                    <p className="mt-0.5 text-xs text-ink-secondary truncate">
                      Assunto: {c.template.subject}
                    </p>
                  )}
                  <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-ink-tertiary">
                    <span>Enviado em: {formatDate(entry.sentAt ?? c?.sentAt)}</span>
                    {c?.template?.name && <span>Template: {c.template.name}</span>}
                  </div>
                  {entry.status === 'FAILED' && entry.errorMessage && (
                    <p className="mt-1 text-xs text-danger">{entry.errorMessage}</p>
                  )}
                </div>
                <div className="shrink-0 flex items-center gap-1.5">
                  <StatusIcon status={entry.status} />
                  <Badge tone={tone}>{label}</Badge>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
